import React, { useState, useEffect } from 'react';
import { ArrowRight, Calculator, Clock, Package } from 'lucide-react';
import { Zap } from 'lucide-react';
import NavigationButtons from '../NavigationButtons/NavigationButtons';

const EnergyAnalytics = () => {
  const [shiftHours, setShiftHours] = useState(8);
  const [partsProduced, setPartsProduced] = useState('');
  const [kilowattCost, setKilowattCost] = useState('');
  const [hourlyConsumption, setHourlyConsumption] = useState(8); // kWh per hour
  const [results, setResults] = useState({
    totalConsumption: 0,
    totalCost: 0,
    consumptionPerPart: 0,
    costPerPart: 0
  });
  const [isCalculated, setIsCalculated] = useState(false);
  const [animateResult, setAnimateResult] = useState(false);

  useEffect(() => {
    if (isCalculated) {
      setAnimateResult(true);
      const timer = setTimeout(() => setAnimateResult(false), 1000);
      return () => clearTimeout(timer);
    }
  }, [isCalculated]);

  const calculateCosts = () => {
    if (partsProduced > 0 && kilowattCost > 0) {
      const totalConsumption = hourlyConsumption * shiftHours;
      const totalCost = totalConsumption * kilowattCost;
      const consumptionPerPart = totalConsumption / partsProduced;
      const costPerPart = totalCost / partsProduced;
      
      setResults({
        totalConsumption,
        totalCost,
        consumptionPerPart,
        costPerPart
      });
      
      setIsCalculated(true);
    }
  };

  return (
    <div className="bg-gradient-to-br from-blue-50 min-h-screen to-indigo-100">
 <div className="flex bg-gray-200 h-28 justify-center w-full items-center pt-3">
          <NavigationButtons />
        </div>      
        <div className="bg-white rounded-xl shadow-lg duration-300 hover:shadow-xl max-w-2xl mt-7 mx-auto overflow-hidden transition-all">
        <div className="bg-indigo-600 p-6 text-white">
          <h1 className="flex text-2xl font-bold items-center">
            <Zap className="mr-2" size={24} /> Energy Analytics Dashboard
          </h1>
          <p className="mt-1 opacity-80">Calculate energy cost per manufactured part</p>
        </div>

        <div className="p-6">
          <div className="space-y-6">
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              {/* Shift Hours Input */}
              <div className="duration-300 hover:scale-105 transition-all">
                <label className="flex text-gray-700 text-sm block font-medium items-center mb-2">
                  <Clock size={16} className="text-indigo-500 mr-2" />
                  Shift Hours
                </label>
                <div className="relative">
                  <input
                    type="number"
                    value={shiftHours}
                    onChange={(e) => setShiftHours(Math.max(1, parseInt(e.target.value) || 1))}
                    className="border border-gray-300 rounded-lg w-full focus:border-indigo-500 focus:ring-indigo-500 px-4 py-2"
                    min="1"
                  />
                  <span className="text-gray-500 absolute right-3 top-2">hrs</span>
                </div>
              </div>

              {/* Parts Produced Input */}
              <div className="duration-300 hover:scale-105 transition-all">
                <label className="flex text-gray-700 text-sm block font-medium items-center mb-2">
                  <Package size={16} className="text-indigo-500 mr-2" />
                  Parts Produced
                </label>
                <input
                  type="number"
                  value={partsProduced}
                  onChange={(e) => setPartsProduced(parseInt(e.target.value) || '')}
                  className="border border-gray-300 rounded-lg w-full focus:border-indigo-500 focus:ring-indigo-500 px-4 py-2"
                  min="1"
                  placeholder="Number of parts"
                />
              </div>
            </div>

            <div className="space-y-4">
              {/* Machine Hourly Consumption */}
              <div className="duration-300 hover:scale-105 transition-all">
                <label className="flex text-gray-700 text-sm block font-medium items-center mb-2">
                  <Zap size={16} className="text-indigo-500 mr-2" />
                  Machine Hourly Consumption
                </label>
                <div className="relative">
                  <input
                    type="number"
                    value={hourlyConsumption}
                    onChange={(e) => setHourlyConsumption(parseFloat(e.target.value) || 0)}
                    className="border border-gray-300 rounded-lg w-full focus:border-indigo-500 focus:ring-indigo-500 px-4 py-2"
                    min="0.1"
                    step="0.1"
                  />
                  <span className="text-gray-500 absolute right-3 top-2">kWh</span>
                </div>
              </div>

              {/* Kilowatt Cost Input */}
              <div className="duration-300 hover:scale-105 transition-all">
                <label className="flex text-gray-700 text-sm block font-medium items-center mb-2">
                  <Zap size={16} className="text-indigo-500 mr-2" />
                  Kilowatt Charge per Cost
                </label>
                <div className="relative">
                  <span className="text-gray-500 absolute left-3 top-2">₹</span>
                  <input
                    type="number"
                    value={kilowattCost}
                    onChange={(e) => setKilowattCost(parseFloat(e.target.value) || '')}
                    className="border border-gray-300 rounded-lg w-full focus:border-indigo-500 focus:ring-indigo-500 pl-8 pr-4 py-2"
                    min="0.01"
                    step="0.01"
                    placeholder="Cost per kWh"
                  />
                </div>
              </div>
            </div>

            <div className="bg-indigo-50 border border-indigo-100 p-4 rounded-lg">
              <p className="text-indigo-700 text-sm">Total Machine Energy Consumption: <span className="font-bold">{(hourlyConsumption * shiftHours).toFixed(1)} kWh</span></p>
            </div>

            {/* Calculate Button */}
            <div className="mt-6">
              <button
                onClick={calculateCosts}
                className="flex bg-indigo-600 justify-center rounded-lg text-white w-full duration-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-opacity-50 hover:bg-indigo-700 items-center py-3 transition-all"
                disabled={!partsProduced || !kilowattCost}
              >
                <Calculator size={20} className="mr-2" />
                Calculate Energy Cost Per Part
                <ArrowRight size={16} className="ml-2" />
              </button>
            </div>
          </div>

          {/* Results Section */}
          {isCalculated && (
            <div className={`mt-8 p-6 border border-indigo-200 bg-indigo-50 rounded-lg ${animateResult ? 'animate-pulse' : ''}`}>
              <h2 className="text-indigo-700 text-lg font-semibold mb-4">Results</h2>
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-white p-4 rounded-lg shadow">
                  <p className="text-gray-500 text-sm">Total Energy Cost</p>
                  <p className="text-indigo-600 text-xl font-bold">
                    ₹{results.totalCost.toFixed(2)}
                  </p>
                </div>
                <div className="bg-white p-4 rounded-lg shadow">
                  <p className="text-gray-500 text-sm">Energy Cost Per Part</p>
                  <p className="text-indigo-600 text-xl font-bold">₹{results.costPerPart.toFixed(2)}</p>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4 mt-4">
                <div className="bg-white p-4 rounded-lg shadow">
                  <p className="text-gray-500 text-sm">Total Energy Consumption</p>
                  <p className="text-indigo-600 text-xl font-bold">{results.totalConsumption.toFixed(1)} kWh</p>
                </div>
                <div className="bg-white p-4 rounded-lg shadow">
                  <p className="text-gray-500 text-sm">Energy Per Part</p>
                  <p className="text-indigo-600 text-xl font-bold">{results.consumptionPerPart.toFixed(2)} kWh</p>
                </div>
              </div>
              
              <div className="text-gray-600 text-sm mt-4">
                <p>Based on {hourlyConsumption} kWh hourly consumption over {shiftHours} hours at ₹{kilowattCost} per kWh.</p>
                <p>Producing {partsProduced} parts during this shift.</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EnergyAnalytics;